import {
  createDesignMutationProposal,
  assertDesignMutationExecutable,
  assertDesignMutationProposal,
  computeDesignContentHash,
  type DesignMutation,
  type DesignMutationKind,
  type DesignMutationOperation,
  type DesignMutationProposal,
} from "@cogo/design-data/design-mutation";
import type { LoadedApp } from "@/features/page-editor/store/types";
import type { OpenPencilSourceWorkspace } from "./openpencil-source-workspace";

const CONTENT_HASH_PREFIX = "sha256:";

type JsonRecord = Record<string, unknown>;

export interface OpenPencilSourcePatchOperation {
  operation:
    | "rename-node"
    | "set-text"
    | "set-visible"
    | "set-opacity"
    | "set-solid-fill"
    | "set-geometry";
  nodeId: string;
  sourceNodeId?: string;
  expectedNodeType: string;
  expectedName?: string;
  name?: string;
  text?: string;
  visible?: boolean;
  opacity?: number;
  fillIndex?: number;
  expectedColor?: string;
  color?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  rotation?: number;
}

export interface OpenPencilCogoElementSnapshot {
  blockName: string;
  elementId: string;
  pointer: string;
  before: JsonRecord;
  after: JsonRecord;
}

export interface OpenPencilCogoDesignMutationPlan {
  mutation: DesignMutation;
  sourcePatch: {
    schemaVersion: "cogo.openpencil-fig-edit-patch.v1";
    patchId: string;
    baseSourceId: string;
    baseSha256: string;
    actor: "human" | "agent";
    reason: string;
    operations: OpenPencilSourcePatchOperation[];
    requestedAt: string;
  };
  baseCogoRevision: number;
  nextCogoRevision: number;
  nextProject: LoadedApp;
  elementSnapshots: OpenPencilCogoElementSnapshot[];
  changedPaths: string[];
}

export interface DesignMutationSourceReadback {
  sourceId: string;
  sourceRevision: number;
  sha256: string;
  artifactRef: string;
  changedSurfaceIds: string[];
  visualDiffStatus: string;
  appliedAt: string;
}

export interface OpenPencilCogoDesignMutationDraftOperation {
  operationId: string;
  kind: DesignMutationKind;
  sourceTarget: {
    nodeId: string;
    expectedNodeType: string;
    expectedName?: string;
  };
  cogoTarget: {
    blockName: string;
    elementId: string;
  };
  nextValue: unknown;
}

export interface FinalizedOpenPencilCogoDesignMutation {
  project: LoadedApp;
  elementSnapshots: OpenPencilCogoElementSnapshot[];
  projectSha256: string;
  readback: {
    schemaVersion: "cogo.studio.design-mutation-readback.v1";
    mutationId: string;
    mutationHash: string;
    cogoRevision: number;
    source: Omit<
      DesignMutationSourceReadback,
      "changedSurfaceIds" | "visualDiffStatus" | "appliedAt"
    >;
    changedPaths: string[];
    changedSurfaceIds: string[];
    visualDiffStatus: string;
    appliedAt: string;
  };
}

export interface CogoDesignMutationElementReplacement {
  blockName: string;
  elementId: string;
  element: JsonRecord;
}

export interface OpenPencilCogoDesignMutationPreviewChange {
  path: string;
  before?: unknown;
  proposed?: unknown;
}

export interface OpenPencilCogoDesignMutationImpactPreview {
  changedPaths: string[];
  changes: OpenPencilCogoDesignMutationPreviewChange[];
}

export const OPENPENCIL_DESIGN_MUTATION_HISTORY_PREVIEW = {
  effect: "append-runtime-readback",
  producer: "openpencil-source-revision-runner",
} as const;

export const OPENPENCIL_REVISION_VISUAL_DIFF_PREVIEW = {
  effect: "clear-then-recompute",
  clearValue: null,
  producer: "openpencil-revision-visual-diff-runner",
} as const;

const SOURCE_WORKSPACE_INVALIDATION_PREVIEW = [
  {
    path: "/sourceWorkspace/visualJudgements",
    key: "visualJudgements",
    proposed: {},
  },
  {
    path: "/sourceWorkspace/revisionVisualDiff",
    key: "revisionVisualDiff",
    proposed: OPENPENCIL_REVISION_VISUAL_DIFF_PREVIEW,
  },
  {
    path: "/sourceWorkspace/assemblyManifest",
    key: "assemblyManifest",
    proposed: null,
  },
  {
    path: "/sourceWorkspace/candidateBinding",
    key: "candidateBinding",
    proposed: null,
  },
  {
    path: "/sourceWorkspace/runtimeProof",
    key: "runtimeProof",
    proposed: null,
  },
] as const satisfies ReadonlyArray<{
  path: string;
  key: keyof OpenPencilSourceWorkspace;
  proposed: unknown;
}>;

function record(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};
}

function jsonSnapshot<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function escapePointerToken(value: string): string {
  return value.replace(/~/g, "~0").replace(/\//g, "~1");
}

function readPointer(root: unknown, pointer: string): unknown {
  if (pointer === "") return root;
  if (!pointer.startsWith("/")) {
    throw new Error("DESIGN_MUTATION_COGO_POINTER_INVALID");
  }
  let current = root;
  for (const encoded of pointer.slice(1).split("/")) {
    const key = encoded.replace(/~1/g, "/").replace(/~0/g, "~");
    if (!current || typeof current !== "object") {
      return undefined;
    }
    current = (current as JsonRecord)[key];
  }
  return current;
}

function deepEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => deepEqual(value, right[index]))
    );
  }
  if (
    !left ||
    typeof left !== "object" ||
    !right ||
    typeof right !== "object"
  ) {
    return false;
  }
  const leftEntries = Object.entries(left as JsonRecord);
  const rightRecord = right as JsonRecord;
  return (
    leftEntries.length === Object.keys(rightRecord).length &&
    leftEntries.every(
      ([key, value]) =>
        Object.prototype.hasOwnProperty.call(rightRecord, key) &&
        deepEqual(value, rightRecord[key]),
    )
  );
}

export function cogoDesignMutationValuesEqual(
  left: unknown,
  right: unknown,
): boolean {
  return deepEqual(left, right);
}

function findElementLocations(
  element: JsonRecord,
  elementId: string,
  pointer: string,
  output: Array<{ element: JsonRecord; pointer: string }>,
): void {
  if (element.id === elementId) output.push({ element, pointer });
  const children = Array.isArray(element.children) ? element.children : [];
  children.forEach((child, index) => {
    if (child && typeof child === "object" && !Array.isArray(child)) {
      findElementLocations(
        child as JsonRecord,
        elementId,
        `${pointer}/children/${index}`,
        output,
      );
    }
  });
}

function exactElementTarget(
  project: LoadedApp,
  blockName: string,
  elementId: string,
): { element: JsonRecord; pointer: string } {
  const block = project.blocks[blockName];
  if (!block) throw new Error("DESIGN_MUTATION_COGO_BLOCK_NOT_FOUND");
  const root = record(record(block).ui).root;
  if (!root || typeof root !== "object" || Array.isArray(root)) {
    throw new Error("DESIGN_MUTATION_COGO_ROOT_MISSING");
  }
  const pointer = `/blocks/${escapePointerToken(blockName)}/ui/root`;
  const matches: Array<{ element: JsonRecord; pointer: string }> = [];
  findElementLocations(root as JsonRecord, elementId, pointer, matches);
  if (matches.length === 0) {
    throw new Error("DESIGN_MUTATION_COGO_ELEMENT_NOT_FOUND");
  }
  if (matches.length !== 1) {
    throw new Error("DESIGN_MUTATION_COGO_ELEMENT_AMBIGUOUS");
  }
  return matches[0];
}

function synchronizeFlatBlockRootAlias(
  project: LoadedApp,
  blockName: string,
): void {
  const block = project.blocks[blockName] as unknown as JsonRecord;
  if (!("root" in block)) return;
  block.root = jsonSnapshot(record(block.ui).root);
}

function sourceNodeIds(element: JsonRecord): string[] {
  const identities = [
    record(element.sourceDesign).figmaNodeId,
    record(element.dataAttributes).figmaNodeId,
    element.sourceNodeId,
  ].filter(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );
  return [...new Set(identities)];
}

function expectedPointer(
  operation: DesignMutationOperation,
  elementPointer: string,
): string {
  switch (operation.kind) {
    case "rename-node":
      return `${elementPointer}/name`;
    case "set-text":
      return `${elementPointer}/text`;
    case "set-visible":
      return `${elementPointer}/visible`;
    case "set-opacity":
      return `${elementPointer}/style/opacity`;
    case "set-solid-fill":
      if (operation.nextValue.fillIndex !== 0) {
        throw new Error("DESIGN_MUTATION_PRIMARY_SOLID_FILL_REQUIRED");
      }
      return `${elementPointer}/style/backgroundColor`;
    case "set-geometry":
      return elementPointer;
  }
}

function isDesignMutationKind(value: unknown): value is DesignMutationKind {
  return (
    value === "rename-node" ||
    value === "set-text" ||
    value === "set-visible" ||
    value === "set-opacity" ||
    value === "set-solid-fill" ||
    value === "set-geometry"
  );
}

function changedPathsFor(
  operation: DesignMutationOperation,
  elementPointer: string,
): string[] {
  if (operation.kind === "rename-node") {
    return [
      expectedPointer(operation, elementPointer),
      `${elementPointer}/sourceDesign/figmaNodeName`,
    ];
  }
  if (operation.kind === "set-visible") {
    return [
      expectedPointer(operation, elementPointer),
      `${elementPointer}/sourceDesign/sourceVisible`,
    ];
  }
  if (operation.kind !== "set-geometry") {
    return [expectedPointer(operation, elementPointer)];
  }
  return [
    ...(operation.nextValue.x === undefined
      ? []
      : [`${elementPointer}/style/left`]),
    ...(operation.nextValue.y === undefined
      ? []
      : [`${elementPointer}/style/top`]),
    ...(operation.nextValue.width === undefined
      ? []
      : [`${elementPointer}/style/width`]),
    ...(operation.nextValue.height === undefined
      ? []
      : [`${elementPointer}/style/height`]),
    ...(operation.nextValue.rotation === undefined
      ? []
      : [`${elementPointer}/transform/rotate`]),
    ...(operation.nextValue.x === undefined
      ? []
      : [`${elementPointer}/sourceDesign/absoluteRenderBounds/x`]),
    ...(operation.nextValue.y === undefined
      ? []
      : [`${elementPointer}/sourceDesign/absoluteRenderBounds/y`]),
    ...(operation.nextValue.width === undefined
      ? []
      : [`${elementPointer}/sourceDesign/absoluteRenderBounds/width`]),
    ...(operation.nextValue.height === undefined
      ? []
      : [`${elementPointer}/sourceDesign/absoluteRenderBounds/height`]),
  ];
}

function toSourcePatchOperation(
  operation: DesignMutationOperation,
): OpenPencilSourcePatchOperation {
  const base = {
    operation: operation.kind,
    nodeId: operation.sourceTarget.nodeId,
    sourceNodeId: operation.cogoTarget.sourceNodeId,
    expectedNodeType: operation.sourceTarget.expectedNodeType,
    ...(operation.sourceTarget.expectedName
      ? { expectedName: operation.sourceTarget.expectedName }
      : {}),
  };
  switch (operation.kind) {
    case "rename-node":
      return { ...base, name: operation.nextValue };
    case "set-text":
      return { ...base, text: operation.nextValue };
    case "set-visible":
      return { ...base, visible: operation.nextValue };
    case "set-opacity":
      return { ...base, opacity: operation.nextValue };
    case "set-solid-fill":
      return { ...base, ...operation.nextValue };
    case "set-geometry":
      return { ...base, ...operation.nextValue };
  }
}

function applyOperation(
  element: JsonRecord,
  operation: DesignMutationOperation,
) {
  const sourceDesign = record(element.sourceDesign);
  switch (operation.kind) {
    case "rename-node":
      element.name = operation.nextValue;
      element.sourceDesign = {
        ...sourceDesign,
        figmaNodeName: operation.nextValue,
      };
      return;
    case "set-text":
      element.text = operation.nextValue;
      return;
    case "set-visible":
      element.visible = operation.nextValue;
      element.sourceDesign = {
        ...sourceDesign,
        sourceVisible: operation.nextValue,
      };
      return;
    case "set-opacity":
      element.style = {
        ...record(element.style),
        opacity: operation.nextValue,
      };
      return;
    case "set-solid-fill":
      element.style = {
        ...record(element.style),
        backgroundColor: operation.nextValue.color,
      };
      return;
    case "set-geometry": {
      const next = operation.nextValue;
      element.style = {
        ...record(element.style),
        ...(next.x === undefined ? {} : { left: next.x }),
        ...(next.y === undefined ? {} : { top: next.y }),
        ...(next.width === undefined ? {} : { width: next.width }),
        ...(next.height === undefined ? {} : { height: next.height }),
      };
      if (next.rotation !== undefined) {
        element.transform = {
          ...record(element.transform),
          rotate: next.rotation,
        };
      }
      element.sourceDesign = {
        ...sourceDesign,
        absoluteRenderBounds: {
          ...record(sourceDesign.absoluteRenderBounds),
          ...(next.x === undefined ? {} : { x: next.x }),
          ...(next.y === undefined ? {} : { y: next.y }),
          ...(next.width === undefined ? {} : { width: next.width }),
          ...(next.height === undefined ? {} : { height: next.height }),
        },
      };
    }
  }
}

function mutationRevisionsInElement(element: JsonRecord): number[] {
  const history = record(element.sourceDesign).designMutationHistory;
  const current = Array.isArray(history)
    ? history
        .map((entry) => record(entry).cogoRevision)
        .filter(
          (revision): revision is number =>
            typeof revision === "number" && Number.isInteger(revision),
        )
    : [];
  const children = Array.isArray(element.children) ? element.children : [];
  return [
    ...current,
    ...children.flatMap((child) =>
      child && typeof child === "object" && !Array.isArray(child)
        ? mutationRevisionsInElement(child as JsonRecord)
        : [],
    ),
  ];
}

export function readCogoDesignMutationRevision(project: LoadedApp): number {
  const metadataRevision = record(project.metadata).designMutationRevision;
  const revisions = Object.values(project.blocks).flatMap((block) => {
    const root = record(record(block).ui).root;
    return root && typeof root === "object" && !Array.isArray(root)
      ? mutationRevisionsInElement(root as JsonRecord)
      : [];
  });
  if (
    typeof metadataRevision === "number" &&
    Number.isInteger(metadataRevision) &&
    metadataRevision >= 0
  ) {
    revisions.push(metadataRevision);
  }
  return revisions.length === 0 ? 0 : Math.max(...revisions);
}

export function computeCogoProjectSha256(project: LoadedApp): string {
  return computeDesignContentHash(jsonSnapshot(project)).slice(
    CONTENT_HASH_PREFIX.length,
  );
}

export function proposeOpenPencilCogoDesignMutation(input: {
  mutationId: string;
  actorId: string;
  reason: string;
  operations: OpenPencilCogoDesignMutationDraftOperation[];
  evidenceRefs: string[];
  requestedAt: string;
  workspace: OpenPencilSourceWorkspace;
  currentProjectId: string;
  currentProject: LoadedApp;
}): DesignMutationProposal {
  const bundle = input.workspace.bundle;
  const sourceRevision = bundle.revisionLineage?.sourceRevision ?? 0;
  if (
    bundle.status !== "ready" ||
    input.workspace.sourceId !== bundle.sourceId ||
    !bundle.fileSha256 ||
    !bundle.sourceArtifactRef ||
    !bundle.fileName.toLowerCase().endsWith(".fig")
  ) {
    throw new Error("DESIGN_MUTATION_EDITABLE_OPENPENCIL_SOURCE_REQUIRED");
  }
  if (!Array.isArray(input.operations) || input.operations.length === 0) {
    throw new Error("DESIGN_MUTATION_DRAFT_OPERATIONS_REQUIRED");
  }
  const operations = input.operations.map((draft) => {
    if (!isDesignMutationKind(draft.kind)) {
      throw new Error("INVALID_DESIGN_MUTATION_KIND");
    }
    const target = exactElementTarget(
      input.currentProject,
      draft.cogoTarget.blockName,
      draft.cogoTarget.elementId,
    );
    const identities = sourceNodeIds(target.element);
    if (
      identities.length !== 1 ||
      identities[0] !== draft.sourceTarget.nodeId
    ) {
      throw new Error("DESIGN_MUTATION_SOURCE_NODE_IDENTITY_MISMATCH");
    }
    const pointer = expectedPointer(
      {
        kind: draft.kind,
        nextValue: draft.nextValue,
      } as DesignMutationOperation,
      target.pointer,
    );
    const expectedValue = readPointer(input.currentProject, pointer);
    return {
      operationId: draft.operationId,
      kind: draft.kind,
      sourceTarget: { ...draft.sourceTarget },
      cogoTarget: {
        blockName: draft.cogoTarget.blockName,
        elementId: draft.cogoTarget.elementId,
        sourceNodeId: identities[0],
        jsonPointer: pointer,
        ...(expectedValue === undefined
          ? {}
          : { expectedValue: jsonSnapshot(expectedValue) }),
      },
      nextValue: jsonSnapshot(draft.nextValue),
    } as DesignMutationOperation;
  });
  if (
    new Set(operations.map((operation) => operation.cogoTarget.blockName))
      .size !== 1
  ) {
    throw new Error("DESIGN_MUTATION_SINGLE_BLOCK_SCOPE_REQUIRED");
  }
  return createDesignMutationProposal({
    mutationId: input.mutationId,
    source: {
      kind: "openpencil-revision",
      sourceId: bundle.sourceId,
      sourceRevision,
      sha256: bundle.fileSha256,
      artifactRef: bundle.sourceArtifactRef,
      ...(input.workspace.figmaCompanion.upstreamFigma
        ? {
            upstreamFigma: {
              ...input.workspace.figmaCompanion.upstreamFigma,
            },
          }
        : {}),
    },
    baseCogoProject: {
      projectId: input.currentProjectId,
      revision: readCogoDesignMutationRevision(input.currentProject),
      sha256: computeCogoProjectSha256(input.currentProject),
    },
    actor: { kind: "agent", id: input.actorId },
    reason: input.reason,
    operations,
    requestedAt: input.requestedAt,
    evidenceRefs: input.evidenceRefs,
  });
}

export function captureCogoDesignMutationElements(
  project: LoadedApp,
  targets: Array<{ blockName: string; elementId: string }>,
): CogoDesignMutationElementReplacement[] {
  return targets.map((target) => ({
    ...target,
    element: jsonSnapshot(
      exactElementTarget(project, target.blockName, target.elementId).element,
    ),
  }));
}

export function restoreCogoDesignMutationElements(input: {
  project: LoadedApp;
  replacements: CogoDesignMutationElementReplacement[];
  metadata: Record<string, unknown> | undefined;
}): LoadedApp {
  const project = jsonSnapshot(input.project);
  const seen = new Set<string>();
  for (const replacement of input.replacements) {
    const key = `${replacement.blockName}\u0000${replacement.elementId}`;
    if (seen.has(key)) {
      throw new Error("DESIGN_MUTATION_RESTORE_TARGET_DUPLICATE");
    }
    seen.add(key);
    const target = exactElementTarget(
      project,
      replacement.blockName,
      replacement.elementId,
    );
    for (const property of Object.keys(target.element)) {
      delete target.element[property];
    }
    Object.assign(target.element, jsonSnapshot(replacement.element));
  }
  for (const blockName of new Set(
    input.replacements.map((replacement) => replacement.blockName),
  )) {
    synchronizeFlatBlockRootAlias(project, blockName);
  }
  project.metadata = input.metadata ? jsonSnapshot(input.metadata) : undefined;
  return project;
}

type OpenPencilCogoMutationAuthority = Pick<
  DesignMutation | DesignMutationProposal,
  "source" | "baseCogoProject" | "operations"
>;

interface OpenPencilCogoMutationProjection {
  baseCogoRevision: number;
  nextCogoRevision: number;
  nextProject: LoadedApp;
  elementSnapshots: OpenPencilCogoElementSnapshot[];
  changedPaths: string[];
}

function assertOpenPencilCogoMutationAuthority(input: {
  mutation: OpenPencilCogoMutationAuthority;
  workspace: OpenPencilSourceWorkspace;
  currentProjectId: string;
  currentProject: LoadedApp;
}): void {
  const { mutation } = input;
  if (
    new Set(
      mutation.operations.map((operation) => operation.cogoTarget.blockName),
    ).size !== 1
  ) {
    throw new Error("DESIGN_MUTATION_SINGLE_BLOCK_SCOPE_REQUIRED");
  }
  if (mutation.source.kind !== "openpencil-revision") {
    throw new Error("DESIGN_MUTATION_EDITABLE_OPENPENCIL_SOURCE_REQUIRED");
  }
  const activeSourceRevision =
    input.workspace.bundle.revisionLineage?.sourceRevision ?? 0;
  if (
    input.workspace.bundle.status !== "ready" ||
    input.workspace.sourceId !== input.workspace.bundle.sourceId ||
    mutation.source.sourceId !== input.workspace.bundle.sourceId ||
    mutation.source.sourceRevision !== activeSourceRevision ||
    mutation.source.sha256 !== input.workspace.bundle.fileSha256 ||
    mutation.source.artifactRef !== input.workspace.bundle.sourceArtifactRef
  ) {
    throw new Error("DESIGN_MUTATION_SOURCE_AUTHORITY_MISMATCH");
  }
  const baseCogoRevision = readCogoDesignMutationRevision(input.currentProject);
  if (
    mutation.baseCogoProject.projectId !== input.currentProjectId ||
    mutation.baseCogoProject.revision !== baseCogoRevision ||
    mutation.baseCogoProject.sha256 !==
      computeCogoProjectSha256(input.currentProject)
  ) {
    throw new Error("DESIGN_MUTATION_COGO_AUTHORITY_MISMATCH");
  }
}

function projectOpenPencilCogoMutation(input: {
  operations: DesignMutationOperation[];
  currentProject: LoadedApp;
}): OpenPencilCogoMutationProjection {
  const baseCogoRevision = readCogoDesignMutationRevision(input.currentProject);
  const initialTargets = input.operations.map((operation) => {
    const target = exactElementTarget(
      input.currentProject,
      operation.cogoTarget.blockName,
      operation.cogoTarget.elementId,
    );
    const identities = sourceNodeIds(target.element);
    if (
      identities.length !== 1 ||
      identities[0] !== operation.cogoTarget.sourceNodeId ||
      operation.sourceTarget.nodeId !== operation.cogoTarget.sourceNodeId
    ) {
      throw new Error("DESIGN_MUTATION_SOURCE_NODE_IDENTITY_MISMATCH");
    }
    const pointer = expectedPointer(operation, target.pointer);
    if (operation.cogoTarget.jsonPointer !== pointer) {
      throw new Error("DESIGN_MUTATION_COGO_POINTER_MISMATCH");
    }
    if (
      operation.cogoTarget.expectedValue !== undefined &&
      !deepEqual(
        readPointer(input.currentProject, pointer),
        operation.cogoTarget.expectedValue,
      )
    ) {
      throw new Error("DESIGN_MUTATION_EXPECTED_VALUE_MISMATCH");
    }
    return { operation, target };
  });

  const nextProject = jsonSnapshot(input.currentProject);
  const touched = new Map<string, OpenPencilCogoElementSnapshot>();
  const changedPaths = new Set<string>();
  for (const { operation, target } of initialTargets) {
    const nextTarget = exactElementTarget(
      nextProject,
      operation.cogoTarget.blockName,
      operation.cogoTarget.elementId,
    );
    const key = `${operation.cogoTarget.blockName}\u0000${operation.cogoTarget.elementId}`;
    const existing = touched.get(key);
    if (!existing) {
      touched.set(key, {
        blockName: operation.cogoTarget.blockName,
        elementId: operation.cogoTarget.elementId,
        pointer: target.pointer,
        before: jsonSnapshot(target.element),
        after: nextTarget.element,
      });
    }
    applyOperation(nextTarget.element, operation);
    for (const path of changedPathsFor(operation, target.pointer)) {
      changedPaths.add(path);
    }
  }
  for (const snapshot of touched.values()) {
    synchronizeFlatBlockRootAlias(nextProject, snapshot.blockName);
  }
  const nextCogoRevision = baseCogoRevision + 1;
  nextProject.metadata = {
    ...record(nextProject.metadata),
    designMutationRevision: nextCogoRevision,
  };
  changedPaths.add("/metadata/designMutationRevision");
  for (const snapshot of touched.values()) {
    snapshot.after = jsonSnapshot(
      exactElementTarget(nextProject, snapshot.blockName, snapshot.elementId)
        .element,
    );
  }
  return {
    baseCogoRevision,
    nextCogoRevision,
    nextProject,
    elementSnapshots: [...touched.values()],
    changedPaths: [...changedPaths],
  };
}

function previewValue(value: unknown): unknown {
  return value === undefined ? undefined : jsonSnapshot(value);
}

/**
 * Builds the confirmation contract from the same exact target validation and
 * Coogo projection used by the executable adapter. Runtime-produced readback is
 * represented as an effect descriptor instead of a guessed final value.
 */
export function buildOpenPencilCogoDesignMutationImpactPreview(input: {
  proposal: unknown;
  workspace: OpenPencilSourceWorkspace;
  currentProjectId: string;
  currentProject: LoadedApp;
}): OpenPencilCogoDesignMutationImpactPreview {
  assertDesignMutationProposal(input.proposal);
  const proposal = input.proposal;
  assertOpenPencilCogoMutationAuthority({
    mutation: proposal,
    workspace: input.workspace,
    currentProjectId: input.currentProjectId,
    currentProject: input.currentProject,
  });
  const projection = projectOpenPencilCogoMutation({
    operations: proposal.operations,
    currentProject: input.currentProject,
  });
  const historyPaths = projection.elementSnapshots.map(
    (snapshot) => `${snapshot.pointer}/sourceDesign/designMutationHistory/-`,
  );
  const cogoPaths = [...projection.changedPaths, ...historyPaths];
  const changes: OpenPencilCogoDesignMutationPreviewChange[] = cogoPaths.map(
    (path) => {
      if (path.endsWith("/sourceDesign/designMutationHistory/-")) {
        return {
          path,
          proposed: OPENPENCIL_DESIGN_MUTATION_HISTORY_PREVIEW,
        };
      }
      const before = readPointer(input.currentProject, path);
      const proposed = readPointer(projection.nextProject, path);
      return {
        path,
        ...(before === undefined ? {} : { before: previewValue(before) }),
        ...(proposed === undefined ? {} : { proposed: previewValue(proposed) }),
      };
    },
  );
  for (const invalidation of SOURCE_WORKSPACE_INVALIDATION_PREVIEW) {
    changes.push({
      path: invalidation.path,
      before: previewValue(input.workspace[invalidation.key]),
      proposed: previewValue(invalidation.proposed),
    });
  }
  return {
    changedPaths: changes.map((change) => change.path),
    changes,
  };
}

export function prepareOpenPencilCogoDesignMutation(input: {
  mutation: unknown;
  workspace: OpenPencilSourceWorkspace;
  currentProjectId: string;
  currentProject: LoadedApp;
}): OpenPencilCogoDesignMutationPlan {
  assertDesignMutationExecutable(input.mutation);
  const mutation = input.mutation;
  if (mutation.source.kind !== "openpencil-revision") {
    throw new Error("DESIGN_MUTATION_EDITABLE_OPENPENCIL_SOURCE_REQUIRED");
  }
  const source = mutation.source;
  assertOpenPencilCogoMutationAuthority({
    mutation,
    workspace: input.workspace,
    currentProjectId: input.currentProjectId,
    currentProject: input.currentProject,
  });
  const projection = projectOpenPencilCogoMutation({
    operations: mutation.operations,
    currentProject: input.currentProject,
  });

  return {
    mutation,
    sourcePatch: {
      schemaVersion: "cogo.openpencil-fig-edit-patch.v1",
      patchId: mutation.mutationId,
      baseSourceId: source.sourceId,
      baseSha256: source.sha256,
      actor: mutation.actor.kind,
      reason: mutation.reason,
      operations: mutation.operations.map(toSourcePatchOperation),
      requestedAt: mutation.requestedAt,
    },
    ...projection,
  };
}

export function finalizeOpenPencilCogoDesignMutation(
  plan: OpenPencilCogoDesignMutationPlan,
  source: DesignMutationSourceReadback,
): FinalizedOpenPencilCogoDesignMutation {
  const project = jsonSnapshot(plan.nextProject);
  const changedPaths = [
    ...new Set([
      ...plan.changedPaths,
      ...plan.elementSnapshots.map(
        (snapshot) =>
          `${snapshot.pointer}/sourceDesign/designMutationHistory/-`,
      ),
    ]),
  ];
  const readback = {
    schemaVersion: "cogo.studio.design-mutation-readback.v1" as const,
    mutationId: plan.mutation.mutationId,
    mutationHash: plan.mutation.contentHash,
    cogoRevision: plan.nextCogoRevision,
    source: {
      sourceId: source.sourceId,
      sourceRevision: source.sourceRevision,
      sha256: source.sha256,
      artifactRef: source.artifactRef,
    },
    changedPaths,
    changedSurfaceIds: [...source.changedSurfaceIds],
    visualDiffStatus: source.visualDiffStatus,
    appliedAt: source.appliedAt,
  };
  const snapshots = plan.elementSnapshots.map((snapshot) => {
    const target = exactElementTarget(
      project,
      snapshot.blockName,
      snapshot.elementId,
    );
    const sourceDesign = record(target.element.sourceDesign);
    const existingHistory = Array.isArray(sourceDesign.designMutationHistory)
      ? sourceDesign.designMutationHistory
      : [];
    target.element.sourceDesign = {
      ...sourceDesign,
      designMutationHistory: [...existingHistory, readback],
    };
    return {
      ...snapshot,
      after: jsonSnapshot(target.element),
    };
  });
  for (const blockName of new Set(
    snapshots.map((snapshot) => snapshot.blockName),
  )) {
    synchronizeFlatBlockRootAlias(project, blockName);
  }
  return {
    project,
    elementSnapshots: snapshots,
    projectSha256: computeCogoProjectSha256(project),
    readback,
  };
}
